import { type CallWindow, Event, Property, User } from '@kalos/kalos-rpc';
import {
  Button,
  Calendar,
  Input,
  LoadingIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from '@kalos/ui';
import { keepPreviousData } from '@tanstack/react-query';
import { addDays, format, getDay, isValid, parseISO } from 'date-fns';
import { AlertTriangle, Loader2, Lock, Pencil, Search, Unlock } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { MAINTENANCE_JOB_TYPE_NAME } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import { useCalendarDataQuery } from '../../../hooks/react-query/useCalendarQuery';
import { useWindowLimitDateOverridesForDate } from '../../../hooks/react-query/useCallWindowLimitDateQuery';
import { useWindowLimitOverrides } from '../../../hooks/react-query/useCallWindowLimitQuery';
import { useCallWindowBatchQuery } from '../../../hooks/react-query/useCallWindowQuery';
import {
  useEventCreateMutation,
  useEventUpdateMutation,
} from '../../../hooks/react-query/useEventsQuery';
import { useJobTypesQuery } from '../../../hooks/react-query/useJobTypesQuery';
import { usePropertyCreateMutation } from '../../../hooks/react-query/usePropertiesQuery';
import { useResolveEventTradeQuery } from '../../../hooks/react-query/useResolveEventTradeQuery';
import { useTimesheetDepartmentListQuery } from '../../../hooks/react-query/useTimesheetDepartmentQuery';
import { useBatchUserQuery, useUserUpdateMutation } from '../../../hooks/react-query/useUserQuery';
import {
  getBlockedJobTypesForWindow,
  isFullyBlocked,
  useWindowBlocksQuery,
  useWindowBlockToggle,
} from '../../../hooks/react-query/useWindowBlockQuery';
import { resolveWindowLimits } from '../../../tools/callWindowLimits';
import { type DirtyModelFields } from '../../../tools/helpers';
import { isEventInWindow, normalizeTime, resolveEventWindow } from '../../../tools/timeUtils';
import {
  type BookingTrade,
  effectiveEventTradeId,
  isMepDepartment,
  MEP_TRADE_IDS,
} from '../../../utils/eventTrade';
import { formatTimeForDisplay, formatTimeWindowLabel } from '../../utils/timeFormat';
import { getDateStatusBreakout } from '../CallWindow/utils';
import { CustomerStep } from './components/CustomerStep';
import { type JobType } from './components/JobTypeSelectionStep';
import { ProjectDetails, type ProjectFormValues } from './components/ProjectDetails';
import { PropertyStep } from './components/PropertyStep';
import { ReviewStep } from './components/ReviewStep';
import { DEFAULT_DEPARTMENT_ID, ServiceCallDetails } from './components/ServiceCallDetails';
import { type JobCreationStep, StepIndicator } from './components/StepIndicator';
import { useSchedulerStyle } from './context/schedulerStyle';
import { resolveTimeWindow } from './hooks/resolveTimeWindow';
import { useEventDraft } from './hooks/useEventDraft';
import { type CustomerLevelData, useEventSubmit } from './hooks/useEventSubmit';
import { useJobCreationPersistence } from './hooks/useJobCreationPersistence';
import { type JobCreationWizardStep } from './types';

export interface JobCreationFlowProps {
  /**
   * Initially selected date in ISO format (yyyy-MM-dd).
   * If not provided, defaults to today.
   */
  date?: string;
  onSubmit: (event: Partial<Event>) => void;
  onCancel?: () => void;
  hideCancelButton?: boolean;
  /**
   * Optional callback fired when the user selects a new date from the calendar.
   * Returns the newly selected date (yyyy-MM-dd).
   */
  onChangeDate?: (date: string) => void;
  initialTimeWindowId?: number;
  windowLabel?: string;
  onJobCreated?: () => void;
  /** Existing job to edit/reschedule */
  existingEvent?: Event;
  /** Function to trigger global loading state */
  triggerGlobalLoading?: (loadingFunction: () => Promise<void>) => Promise<void>;
  /**
   * Whether to show the job type selection step (Service vs Project).
   * Default: true for new jobs, false when editing.
   */
  showTypeSelection?: boolean;
  /**
   * Restrict to only one job type. If set, skips the type selection step.
   */
  allowedJobType?: JobType;
  /**
   * Whether to show the scheduling header banner with date/time info.
   * Default: true when date is provided
   */
  showSchedulingBanner?: boolean;
  /** Callback when the job type changes (e.g. to update dialog title) */
  onJobTypeChange?: (type: JobType | null) => void;
  /** Reports the active display step (search/review/schedule) to the parent. */
  onStepChange?: (step: JobCreationStep) => void;
  /** Hide call window selection and use custom time only (e.g. job info edit dialog) */
  hideCallWindowSelection?: boolean;
  /** Whether to show the block/unblock button in the scheduling banner */
  canBlock?: boolean;
  /** Title displayed on the left of the header row (e.g. "Add Job", "Schedule Call") */
  title?: string;
  /** Default department ID to pre-select (falls back to 20 if not provided) */
  defaultDepartmentId?: number;
  /** The scheduler's trade for that department; a Sub Type takes over once picked. */
  defaultTradeId?: number;
  /** Pre-selected customer (opening from a property/customer page); skips the customer step. */
  initialCustomer?: User | null;
  /** Pre-selected property; with initialCustomer, opens straight to the review step. */
  initialProperty?: Property | null;
  /** Seed values for a new job's draft (e.g. description from a warranty intake). A restored wizard snapshot wins. */
  initialEventDraft?: Partial<Event>;
  initialStep?: JobCreationWizardStep;
  persistWizard?: boolean;
}

/** The window with the same start and end time (e.g. the same slot in another trade). */
const findWindowByTime = (
  windows: CallWindow[],
  { startTime, endTime }: Pick<CallWindow, 'startTime' | 'endTime'>,
) =>
  windows.find(
    (w) =>
      normalizeTime(w.startTime) === normalizeTime(startTime) &&
      normalizeTime(w.endTime) === normalizeTime(endTime),
  );

const JobCreationFlowComponent = ({
  date,
  onSubmit,
  onCancel,
  hideCancelButton = false,
  onChangeDate,
  initialTimeWindowId,
  windowLabel,
  onJobCreated,
  existingEvent,
  triggerGlobalLoading,
  allowedJobType,
  showSchedulingBanner,
  onJobTypeChange,
  onStepChange,
  hideCallWindowSelection = false,
  canBlock = false,
  title,
  // Same default as ServiceCallDetails, so counts, caps and windows use the department the form shows
  defaultDepartmentId = DEFAULT_DEPARTMENT_ID,
  defaultTradeId,
  initialCustomer,
  initialProperty,
  initialEventDraft,
  initialStep,
  persistWizard = true,
}: JobCreationFlowProps) => {
  const auth = useAuth();
  const compact = useSchedulerStyle();
  const isEditing = !!existingEvent;

  // Persist the in-progress new-job wizard so a remount (tab switch, window-focus
  // refetch, auth-token refresh, page refresh) doesn't reset it to step 1 and
  // wipe the user's input. New jobs only — editing/reschedule isn't persisted.
  const {
    restored,
    save: savePersistedDraft,
    clear: clearPersistedDraft,
  } = useJobCreationPersistence(!isEditing && persistWizard);

  // Determine initial step based on props
  const getInitialStep = (): JobCreationWizardStep => {
    if (isEditing) return 'details';
    if (initialStep) return initialStep;
    if (initialProperty && initialCustomer) return 'review';
    if (initialCustomer) return 'property';
    return restored?.step ?? 'customer';
  };

  const [step, setStep] = useState<JobCreationWizardStep>(getInitialStep);
  const [customer, setCustomer] = useState<User | null>(
    () => initialCustomer ?? restored?.customer ?? existingEvent?.customer ?? null,
  );
  const [property, setProperty] = useState<Property | null>(
    () => initialProperty ?? restored?.property ?? existingEvent?.property ?? null,
  );
  const [justCreatedCustomer, setJustCreatedCustomer] = useState(false);

  // Job type selection (service or project)
  const [jobType, setJobType] = useState<JobType | null>(
    () => restored?.jobType ?? allowedJobType ?? (existingEvent?.isProject ? 'project' : null),
  );

  // Get the step for the step indicator (maps internal step to display step)
  const getDisplayStep = (): JobCreationStep => {
    if (step === 'customer' || step === 'property') return 'search';
    if (step === 'review') return 'review';
    return 'schedule';
  };

  // Report the active display step so a parent (e.g. the scheduler dialog) can
  // react to it — used to animate the Schedule Call dialog width per step.
  const displayStep = getDisplayStep();
  useEffect(() => {
    onStepChange?.(displayStep);
  }, [displayStep, onStepChange]);

  // The compact prototype styling applies to Search/Review only; Step 3
  // (Schedule) keeps the pinned header/footer but the original look.
  const compactChrome = compact && displayStep !== 'schedule';

  // Notification update handler — keep local customer/property state in sync after edits
  const handleNotificationSaved = useCallback(
    (type: 'customer' | 'property', newText: string) => {
      if (type === 'customer' && customer) {
        setCustomer({ ...customer, notification: newText });
      } else if (type === 'property' && property) {
        setProperty({ ...property, notification: newText });
      }
    },
    [customer, property],
  );

  const {
    draft: formState,
    setDraft: setFormState,
    updateDraft,
    dirtyFields: preservedDirtyFields,
    mergeDirtyFields,
    resetDirtyFields,
  } = useEventDraft({
    existingEvent,
    initialDraft: restored?.draft ?? initialEventDraft,
    initialDirtyFields: restored?.dirtyFields,
  });

  // Recent-visit callback prompt dismissal — owned here (not in the step
  // components) so a "No" in Step 2 (Review) persists into Step 3 (Schedule)
  // instead of the prompt reappearing. Re-show it when the property changes or
  // the callback flag toggles.
  const [callbackPromptDismissed, setCallbackPromptDismissed] = useState(false);
  useEffect(() => {
    setCallbackPromptDismissed(false);
  }, [property?.id, formState.isCallback]);

  // Maintain the currently selected date locally
  const defaultDate = date || format(new Date(), 'yyyy-MM-dd');
  const [selectedDate, setSelectedDate] = useState<string>(
    () =>
      restored?.selectedDate ??
      (existingEvent ? existingEvent.dateStarted.split(' ')[0] : defaultDate),
  );

  // The date this save will actually land on, which is not always selectedDate. In the job-info
  // edit flow (hideCallWindowSelection) the saved dateStarted comes from formState rather than
  // selectedDate -- see the event build in ServiceCallDetails -- and nothing syncs the two:
  // handleFormDataChange never calls setSelectedDate, and handleDateChange (the only caller) is
  // unreachable there because that flow renders no date picker of its own. So editing the start
  // date leaves selectedDate on the original day. Everything cap-related keys off this instead.
  // Falls back to selectedDate unless formState holds a genuinely parseable date: this feeds the
  // calendar query's range, and a half-typed value reaching parseISO/format would throw inside a
  // memo rather than just misreport a cap.
  const capacityDate = useMemo(() => {
    if (!hideCallWindowSelection || !formState.dateStarted) return selectedDate;
    const candidate = formState.dateStarted.split(' ')[0];
    return isValid(parseISO(candidate)) ? candidate : selectedDate;
  }, [hideCallWindowSelection, formState.dateStarted, selectedDate]);

  // Customer search state for preserving user input
  const [customerSearch, setCustomerSearch] = useState<string>(
    () => restored?.customerSearch ?? '',
  );

  // Track the currently selected window ID to preserve time selection across date changes
  const [currentWindowId, setCurrentWindowId] = useState<number | undefined>(
    existingEvent ? undefined : initialTimeWindowId,
  );

  // -------------- Mutations --------------
  const eventCreateMutation = useEventCreateMutation();
  const eventUpdateMutation = useEventUpdateMutation();
  const updateCustomerMutation = useUserUpdateMutation();
  const propertyCreateMutation = usePropertyCreateMutation();

  // -------------------- Data fetching --------------------
  // 1. Call windows (active)
  const {
    data: callWindowData,
    isLoading: isCallWindowLoading,
    refetch: refetchCallWindows,
  } = useCallWindowBatchQuery({
    filter: { isActive: true },
    sort: { orderBy: 'start_time', orderDir: 'ASC' },
    page: -1,
    options: {
      refetchOnWindowFocus: false,
    },
  });

  // 1b. Window blocks for the selected date — filtered by the form's current department
  // so we only fetch blocks relevant to this job (other depts' blocks are irrelevant here).
  const { data: windowBlocksData } = useWindowBlocksQuery({
    dateStart: selectedDate,
    dateEnd: format(addDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'),
    departmentId: formState.departmentId ?? defaultDepartmentId,
  });
  const windowBlocks = useMemo(() => windowBlocksData?.results ?? [], [windowBlocksData]);

  // 1b-ii. Per-department limit overrides, keyed by call_window_id — needed to read a
  // window's effective max before Step 3 renders the per-window cards. Shares its fetch with
  // ServiceCallDetails, which uses the same hook.
  const windowLimitOverrides = useWindowLimitOverrides();
  // Keyed off capacityDate rather than selectedDate for the same reason the counts above are:
  // this is the day the save lands on.
  const windowLimitDateOverrides = useWindowLimitDateOverridesForDate({ date: capacityDate });

  // 1c. Calendar data for overbooking capacity checks
  // Spans selectedDate and capacityDate so the daily-cap check has counts for the day the save
  // lands on, not just the day the wizard is looking at. Identical to a single-day fetch whenever
  // the two agree, which is every flow except a date edit in the job-info dialog; at most two days
  // otherwise. Safe to widen because every other consumer of calendarData filters to selectedDate.
  const calendarReq = useMemo(() => {
    const [rangeStart, rangeEnd] =
      capacityDate < selectedDate ? [capacityDate, selectedDate] : [selectedDate, capacityDate];
    const req = Event.create();
    req.dateStarted = rangeStart;
    req.dateEnded = format(addDays(parseISO(rangeEnd), 1), 'yyyy-MM-dd');
    req.isActive = 1;
    req.orderBy = 'time_started';
    req.orderDir = 'ASC';
    return req;
  }, [selectedDate, capacityDate]);

  const {
    data: calendarData,
    isLoading: isCalendarLoading,
    refetch: refetchCalendar,
  } = useCalendarDataQuery(calendarReq, {
    refetchOnWindowFocus: false,
    // Keep previous calendar data during refetch so we don't drop the form
    placeholderData: keepPreviousData,
  });

  // 2. Timesheet departments (active, non-team)
  const {
    data: departmentData,
    isLoading: isDeptLoading,
    refetch: refetchDepartments,
  } = useTimesheetDepartmentListQuery({
    filter: { isActive: 1, teamOnly: false, fieldMask: ['TeamOnly'] },
    options: {
      refetchOnWindowFocus: false,
    },
  });

  // 3. Assignable technicians (active employees)
  const {
    data: userData,
    isLoading: isUsersLoading,
    refetch: refetchUsers,
  } = useBatchUserQuery({
    filters: {
      isEmployee: 1,
      isActive: 1,
      overrideLimit: true,
      orderBy: 'user_lastname',
      orderDir: 'ASC',
    },
    options: {
      refetchOnWindowFocus: false,
    },
  });

  const callWindows = useMemo<CallWindow[]>(() => callWindowData?.results ?? [], [callWindowData]);
  const departments = departmentData?.results ?? [];
  const technicians = userData?.results ?? [];

  const { data: jobTypesData } = useJobTypesQuery();
  const totalJobTypeCount = jobTypesData?.length ?? 0;

  const { submitJob, submitProject, isSubmitting } = useEventSubmit({
    auth,
    existingEvent,
    isEditing,
    selectedDate,
    property,
    preservedDirtyFields,
    resetDirtyFields,
    onSubmit,
    onJobCreated,
    onCancel,
    clearPersistedDraft,
    eventCreateMutation,
    eventUpdateMutation,
  });

  // Persist the wizard snapshot on every change so an unexpected remount can
  // restore it. Synchronous (no debounce) so the latest keystroke is never the
  // one that's lost. savePersistedDraft is inert when editing.
  // Not during or after a save: a re-render as the dialog closes would restore the draft.
  useEffect(() => {
    if (isSubmitting) return;
    savePersistedDraft({
      step,
      customer,
      property,
      jobType,
      selectedDate,
      customerSearch,
      draft: formState,
      dirtyFields: preservedDirtyFields,
    });
  }, [
    isSubmitting,
    savePersistedDraft,
    step,
    customer,
    property,
    jobType,
    selectedDate,
    customerSearch,
    formState,
    preservedDirtyFields,
  ]);

  // Clear the persisted draft when the user explicitly abandons the wizard.
  const handleCancel = useCallback(() => {
    clearPersistedDraft();
    onCancel?.();
  }, [clearPersistedDraft, onCancel]);

  // Only block the UI when we truly have no data yet; allow refetches to be non-blocking
  const loading =
    (isCallWindowLoading && !callWindowData) ||
    (isDeptLoading && !departmentData) ||
    (isUsersLoading && !userData) ||
    (isCalendarLoading && !calendarData) ||
    isSubmitting ||
    propertyCreateMutation.isPending;

  // -------------------- Booking trade --------------------
  // The Sub Type's trade once one is picked, else the caller's trade, else the department default.
  const bookingDeptId = formState.departmentId ?? defaultDepartmentId ?? 0;
  // Same key as the Sub Type's TradeChip in ServiceCallDetails
  const tradeResolution = useResolveEventTradeQuery({
    departmentId: formState.jobSubtypeId ? bookingDeptId : 0,
    jobTypeId: formState.jobTypeId ?? existingEvent?.jobTypeId ?? 0,
    jobSubtypeId: formState.jobSubtypeId ?? 0,
    // -1 = the form's unset sector
    isResidential: formState.isResidential ?? existingEvent?.isResidential ?? -1,
  }).data;
  const resolvedTradeId = formState.jobSubtypeId
    ? tradeResolution?.eventTradeId || undefined
    : undefined;
  const bookingTrade = useMemo<BookingTrade>(() => {
    const dept = departmentData?.results.find((d) => d.id === bookingDeptId);
    const deptTradeId = dept?.eventTradeId ?? 0;
    const multiTrade = isMepDepartment(bookingDeptId);
    const tradeIds: number[] = multiTrade ? [...MEP_TRADE_IDS] : [deptTradeId];
    const fallback =
      [defaultTradeId, existingEvent?.eventTradeId].find((id) => !!id && tradeIds.includes(id)) ??
      deptTradeId;
    const tradeId =
      resolvedTradeId && tradeIds.includes(resolvedTradeId) ? resolvedTradeId : fallback;
    return { tradeId, deptTradeId, multiTrade };
  }, [departmentData, bookingDeptId, defaultTradeId, existingEvent?.eventTradeId, resolvedTradeId]);

  // In a multi-trade department only the booking trade's jobs count toward its windows and caps.
  const inBookingTrade = useCallback(
    (ev: Event) =>
      !bookingTrade.multiTrade ||
      (ev.departmentId || 0) !== bookingDeptId ||
      effectiveEventTradeId(ev, bookingTrade.deptTradeId) === bookingTrade.tradeId,
    [bookingTrade, bookingDeptId],
  );

  // -------------------- Shared day windows (memoized once, used everywhere) --------------------
  // If dept is configured (has windows for any day), use only its windows — no global fallback.
  // Missing days are intentional. Unconfigured depts fall back to global (dept 0).
  const callWindowsForDept = useMemo(() => {
    const deptId = formState.departmentId ?? defaultDepartmentId;
    if (!deptId) return callWindows.filter((w) => w.departmentId === 0);
    const isConfigured = callWindows.some((w) => w.departmentId === deptId);
    if (!isConfigured) return callWindows.filter((w) => w.departmentId === 0);
    return callWindows.filter(
      (w) =>
        w.departmentId === deptId &&
        (bookingTrade.deptTradeId === 0 ||
          effectiveEventTradeId(w, bookingTrade.deptTradeId) === bookingTrade.tradeId),
    );
  }, [callWindows, formState.departmentId, defaultDepartmentId, bookingTrade]);

  // Filtered windows for the selected day (no sorting)
  const dayWindowsForSelectedDate = useMemo(() => {
    if (!callWindowsForDept.length) return [];
    const dayOfWeek = getDay(parseISO(selectedDate));
    return callWindowsForDept.filter((w) => Number(w.dayOfWeek) === dayOfWeek);
  }, [callWindowsForDept, selectedDate]);

  // Ascending (earliest first) - for UI display & window selection
  const dayWindowsAscending = useMemo(
    () => [...dayWindowsForSelectedDate].sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [dayWindowsForSelectedDate],
  );

  // Descending (latest first) - for event matching priority
  const dayWindowsDescending = useMemo(
    () => [...dayWindowsForSelectedDate].sort((a, b) => b.startTime.localeCompare(a.startTime)),
    [dayWindowsForSelectedDate],
  );
  // -------------------- Window selection logic --------------------
  // Find the appropriate time window for the current date and existing event
  const timeWindowResolution = useMemo(
    () =>
      resolveTimeWindow({
        dayWindowsAscending,
        callWindows,
        selectedDate,
        currentWindowId,
        formTimeStarted: formState.timeStarted,
        formTimeEnded: formState.timeEnded,
        existingEvent,
        initialTimeWindowId,
        isEditing,
        windowBlocks,
        totalJobTypeCount,
      }),
    [
      dayWindowsAscending,
      callWindows,
      selectedDate,
      currentWindowId,
      formState.timeStarted,
      formState.timeEnded,
      existingEvent,
      initialTimeWindowId,
      isEditing,
      windowBlocks,
      totalJobTypeCount,
    ],
  );
  const { effectiveTimeWindow, nextWindowId, type: timeWindowType } = timeWindowResolution;

  // A trade or department switch swaps the day's window rows; keep the same slot on the new row.
  useEffect(() => {
    if (!currentWindowId || dayWindowsForSelectedDate.some((w) => w.id === currentWindowId)) return;
    const previous = callWindows.find((w) => w.id === currentWindowId);
    const match = previous && findWindowByTime(dayWindowsForSelectedDate, previous);
    if (match) setCurrentWindowId(match.id);
  }, [currentWindowId, dayWindowsForSelectedDate, callWindows]);

  useEffect(() => {
    if (timeWindowType === 'custom') {
      // Clear currentWindowId when custom time is set
      // This prevents the FIRST PRIORITY check from selecting a preset window
      if (currentWindowId !== undefined) {
        setCurrentWindowId(undefined);
      }
    } else if (nextWindowId && nextWindowId !== currentWindowId) {
      setCurrentWindowId(nextWindowId);
    }
  }, [nextWindowId, currentWindowId, timeWindowType]);

  // Block/unblock toggle for the scheduling banner button
  const { isBusy: isBlockBusy, toggle: handleToggleBlock } = useWindowBlockToggle({
    date: selectedDate,
    departmentId: formState.departmentId ?? defaultDepartmentId,
    startTime: effectiveTimeWindow?.startTime,
    endTime: effectiveTimeWindow?.endTime,
    enabled: canBlock,
  });

  // -------------------- Overbooking calculations --------------------
  const windowEventCounts = useMemo(() => {
    const counts: Record<number, Record<number, number>> = {};
    if (!calendarData?.dates || dayWindowsDescending.length === 0) return counts;

    const allEvents: Event[] = [];
    Object.entries(calendarData.dates).forEach(([d, day]: [string, any]) => {
      if (d !== selectedDate) return;

      day.serviceCalls.forEach((e: Event) => {
        if (e.logJobStatus !== 'Canceled') {
          allEvents.push({ ...e, dateStarted: d });
        }
      });

      // CHECK IF WE NEED TO INCLUDE COMPLETED SERVICE CALLS
      if (day.completedServiceCalls) {
        day.completedServiceCalls.forEach((e: Event) => {
          if (e.logJobStatus !== 'Canceled') {
            allEvents.push({ ...e, dateStarted: d });
          }
        });
      }
    });

    allEvents.forEach((ev) => {
      if (!inBookingTrade(ev)) return;
      const match = dayWindowsDescending.find((w) => isEventInWindow(ev, w));

      if (match) {
        const deptId = ev.departmentId || 0;
        if (!counts[match.id]) counts[match.id] = {};
        counts[match.id][deptId] = (counts[match.id][deptId] || 0) + 1;
      }
    });

    // If editing, subtract the current event from the counts.
    // Guard on the exact date (not just day-of-week): the counts above only include
    // events where `d === selectedDate`, so subtracting on any other date would remove
    // a job that was never counted — producing an off-by-one on reschedule.
    if (existingEvent && inBookingTrade(existingEvent)) {
      if (existingEvent.dateStarted.split(' ')[0] === selectedDate) {
        // Use the same window matching logic as the counting logic
        const matchWin = dayWindowsDescending.find((w) => isEventInWindow(existingEvent, w));
        if (matchWin && counts[matchWin.id]) {
          const deptId = existingEvent.departmentId || 0;
          if (counts[matchWin.id][deptId]) {
            counts[matchWin.id][deptId] = Math.max(0, counts[matchWin.id][deptId] - 1);
          }
        }
      }
    }

    return counts;
  }, [calendarData, dayWindowsDescending, selectedDate, existingEvent, inBookingTrade]);

  // -------------------- Per-job-type breakdown --------------------
  // Mirrors windowEventCounts but keeps the job-type dimension so each slot card
  // can show a breakdown (e.g. "Service 3 · Maintenance 2") for the selected department.
  const windowJobTypeBreakdown = useMemo(() => {
    const counts: Record<number, Record<number, Record<string, number>>> = {};
    if (!calendarData?.dates || dayWindowsDescending.length === 0) return counts;

    const allEvents: Event[] = [];
    Object.entries(calendarData.dates).forEach(([d, day]: [string, any]) => {
      if (d !== selectedDate) return;

      day.serviceCalls.forEach((e: Event) => {
        if (e.logJobStatus !== 'Canceled') {
          allEvents.push({ ...e, dateStarted: d });
        }
      });

      if (day.completedServiceCalls) {
        day.completedServiceCalls.forEach((e: Event) => {
          if (e.logJobStatus !== 'Canceled') {
            allEvents.push({ ...e, dateStarted: d });
          }
        });
      }
    });

    allEvents.forEach((ev) => {
      if (!inBookingTrade(ev)) return;
      const match = dayWindowsDescending.find((w) => isEventInWindow(ev, w));

      if (match) {
        const deptId = ev.departmentId || 0;
        const jobType = ev.jobType || 'Unspecified';
        if (!counts[match.id]) counts[match.id] = {};
        if (!counts[match.id][deptId]) counts[match.id][deptId] = {};
        counts[match.id][deptId][jobType] = (counts[match.id][deptId][jobType] || 0) + 1;
      }
    });

    // If editing, subtract the current event from the breakdown (matches windowEventCounts)
    if (existingEvent && inBookingTrade(existingEvent)) {
      const dowEvent = getDay(parseISO(existingEvent.dateStarted.split(' ')[0]));
      const selectedDow = getDay(parseISO(selectedDate));
      if (dowEvent === selectedDow) {
        const matchWin = dayWindowsDescending.find((w) => isEventInWindow(existingEvent, w));
        if (matchWin && counts[matchWin.id]) {
          const deptId = existingEvent.departmentId || 0;
          const jobType = existingEvent.jobType || 'Unspecified';
          if (counts[matchWin.id][deptId]?.[jobType]) {
            counts[matchWin.id][deptId][jobType] = Math.max(
              0,
              counts[matchWin.id][deptId][jobType] - 1,
            );
          }
        }
      }
    }

    return counts;
  }, [calendarData, dayWindowsDescending, selectedDate, existingEvent, inBookingTrade]);

  // Per-window count of CONFIRMED maintenance jobs (window -> dept -> count).
  // Drives the maintenance window-limit hard block, which per the doneness
  // criteria only counts confirmed jobs.
  const windowMaintConfirmedCounts = useMemo(() => {
    const counts: Record<number, Record<number, number>> = {};
    if (!calendarData?.dates || dayWindowsDescending.length === 0) return counts;

    const allEvents: Event[] = [];
    Object.entries(calendarData.dates).forEach(([d, day]: [string, any]) => {
      if (d !== selectedDate) return;
      day.serviceCalls.forEach((e: Event) => allEvents.push({ ...e, dateStarted: d }));
      if (day.completedServiceCalls) {
        day.completedServiceCalls.forEach((e: Event) => allEvents.push({ ...e, dateStarted: d }));
      }
    });

    allEvents.forEach((ev) => {
      if (ev.jobType !== MAINTENANCE_JOB_TYPE_NAME || ev.logJobStatus !== 'Confirmed') return;
      if (!inBookingTrade(ev)) return;
      const match = dayWindowsDescending.find((w) => isEventInWindow(ev, w));
      if (!match) return;
      const deptId = ev.departmentId || 0;
      if (!counts[match.id]) counts[match.id] = {};
      counts[match.id][deptId] = (counts[match.id][deptId] || 0) + 1;
    });

    // If editing, subtract the current event from the counts (matches windowEventCounts).
    // Without this a confirmed maintenance job counts against its own window's limit and
    // blocks itself: the window renders as "Maint limit reached" and handleSubmit refuses
    // to save the job back into the window it already occupies.
    if (existingEvent && inBookingTrade(existingEvent)) {
      if (
        existingEvent.jobType === MAINTENANCE_JOB_TYPE_NAME &&
        existingEvent.logJobStatus === 'Confirmed' &&
        existingEvent.dateStarted.split(' ')[0] === selectedDate
      ) {
        const matchWin = dayWindowsDescending.find((w) => isEventInWindow(existingEvent, w));
        if (matchWin && counts[matchWin.id]) {
          const deptId = existingEvent.departmentId || 0;
          if (counts[matchWin.id][deptId]) {
            counts[matchWin.id][deptId] = Math.max(0, counts[matchWin.id][deptId] - 1);
          }
        }
      }
    }

    return counts;
  }, [calendarData, dayWindowsDescending, selectedDate, existingEvent, inBookingTrade]);

  // Day-level count of CONFIRMED jobs per job type (dept -> job type name -> count), for the
  // daily job-type caps. Deliberately not per-window: a cap is a ceiling for the whole day
  // across every window, unlike the per-window maint_max above.
  //
  // Confirmed-only to match the week-view "N / M" header counter
  // (CallWindowColumn.tsx:130-136), which reads against the same cap -- if the two counted
  // differently, the header and the block would disagree about whether the day is full.
  //
  // Keyed by job type NAME because that is what Event carries; callers map id -> name.
  //
  // Scoped to capacityDate rather than selectedDate: the cap has to be measured on the day the
  // save actually lands on, which differs when the job-info edit dialog changes the start date.
  const dayJobTypeConfirmedCounts = useMemo(() => {
    const counts: Record<number, Record<string, number>> = {};
    if (!calendarData?.dates) return counts;

    const allEvents: Event[] = [];
    Object.entries(calendarData.dates).forEach(([d, day]: [string, any]) => {
      if (d !== capacityDate) return;
      day.serviceCalls.forEach((e: Event) => allEvents.push({ ...e, dateStarted: d }));
      if (day.completedServiceCalls) {
        day.completedServiceCalls.forEach((e: Event) => allEvents.push({ ...e, dateStarted: d }));
      }
    });

    allEvents.forEach((ev) => {
      if (ev.logJobStatus !== 'Confirmed' || !inBookingTrade(ev)) return;
      const jobType = ev.jobType || 'Unspecified';
      const deptId = ev.departmentId || 0;
      if (!counts[deptId]) counts[deptId] = {};
      counts[deptId][jobType] = (counts[deptId][jobType] || 0) + 1;
    });

    // Editing an already-confirmed job on this date: don't let it count against its own cap,
    // which would block rescheduling it within the same day. Mirrors the same adjustment in
    // windowMaintConfirmedCounts above, minus the window dimension.
    if (
      existingEvent &&
      inBookingTrade(existingEvent) &&
      existingEvent.logJobStatus === 'Confirmed' &&
      existingEvent.dateStarted.split(' ')[0] === capacityDate
    ) {
      const jobType = existingEvent.jobType || 'Unspecified';
      const deptId = existingEvent.departmentId || 0;
      if (counts[deptId]?.[jobType]) {
        counts[deptId][jobType] = Math.max(0, counts[deptId][jobType] - 1);
      }
    }

    return counts;
  }, [calendarData, capacityDate, existingEvent, inBookingTrade]);

  // -------------------- Daily category pill breakout --------------------
  // Scoped to the job's department so the counts match the day-view department-column
  // header in the scheduler grid (which is also per-department).
  const dateBreakout = useMemo(() => {
    const departmentId = formState.departmentId ?? defaultDepartmentId;
    const day = calendarData?.dates?.[selectedDate];
    const dayEvents: Event[] = [];
    const keep = (e: Event) =>
      e.logJobStatus !== 'Canceled' && e.departmentId === departmentId && inBookingTrade(e);
    day?.serviceCalls?.forEach((e: Event) => {
      if (keep(e)) dayEvents.push(e);
    });
    day?.completedServiceCalls?.forEach((e: Event) => {
      if (keep(e)) dayEvents.push(e);
    });
    return getDateStatusBreakout(dayEvents);
  }, [calendarData, selectedDate, formState.departmentId, defaultDepartmentId, inBookingTrade]);

  // -------------------- Tech assignment calculations --------------------
  const windowTechCounts = useMemo(() => {
    const techCounts: Record<number, Record<number, Set<number>>> = {};
    if (!calendarData?.dates || dayWindowsDescending.length === 0) return {};

    const allEvents: Event[] = [];
    Object.entries(calendarData.dates).forEach(([d, day]: [string, any]) => {
      if (d !== selectedDate) return;
      day.serviceCalls.forEach((e: Event) => {
        if (e.logJobStatus !== 'Canceled') {
          allEvents.push({ ...e, dateStarted: d });
        }
      });

      // Include completed jobs too so their assigned techs are counted (matches windowEventCounts)
      if (day.completedServiceCalls) {
        day.completedServiceCalls.forEach((e: Event) => {
          if (e.logJobStatus !== 'Canceled') {
            allEvents.push({ ...e, dateStarted: d });
          }
        });
      }
    });

    // Count unique technicians by window and department (using shared dayWindowsDescending)
    allEvents.forEach((ev) => {
      if (!inBookingTrade(ev)) return;
      const match = dayWindowsDescending.find((w) => isEventInWindow(ev, w));
      if (match && ev.logTechnicianAssigned) {
        const deptId = ev.departmentId || 0;
        if (!techCounts[match.id]) techCounts[match.id] = {};
        if (!techCounts[match.id][deptId]) techCounts[match.id][deptId] = new Set();

        // Parse comma-separated tech IDs and add to set
        const techIds = ev.logTechnicianAssigned
          .split(',')
          .map((id) => Number(id.trim()))
          .filter((id) => !isNaN(id) && id > 0);

        techIds.forEach((techId) => {
          techCounts[match.id][deptId].add(techId);
        });
      }
    });

    // Convert Sets to counts for easier consumption
    const result: Record<number, Record<number, number>> = {};
    Object.entries(techCounts).forEach(([windowId, deptMap]) => {
      result[Number(windowId)] = {};
      Object.entries(deptMap).forEach(([deptId, techSet]) => {
        result[Number(windowId)][Number(deptId)] = techSet.size;
      });
    });

    return result;
  }, [calendarData, dayWindowsDescending, selectedDate, inBookingTrade]);

  // Handle date change - just update the view, mark as dirty for later save
  const handleDateChange = useCallback(
    (newDate: string) => {
      setSelectedDate(newDate);

      // Clear the current window selection to allow soonest available time to be selected
      setCurrentWindowId(undefined);

      // If editing an existing event, mark the time window as dirty so it gets included in the update
      // But DON'T save yet - that happens on submit
      if (isEditing && existingEvent) {
        // Mark timeWindowId as dirty to ensure the time change is tracked when user submits
        mergeDirtyFields({ timeWindowId: true });

        // Find the new window for the selected date
        const newDayOfWeek = getDay(parseISO(newDate));
        const newDayWindows = callWindows
          .filter((w) => Number(w.dayOfWeek) === newDayOfWeek)
          .sort((a, b) => a.startTime.localeCompare(b.startTime));

        if (newDayWindows.length > 0) {
          // Helper to check if a window is past for the new date
          const now = new Date();
          const currentDate = format(now, 'yyyy-MM-dd');
          const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

          const isPastWindowForDate = (window: CallWindow, dateToCheck: string) => {
            if (dateToCheck < currentDate) return true;
            if (dateToCheck === currentDate) {
              const windowStartMinutes =
                parseInt(window.startTime.split(':')[0]) * 60 +
                parseInt(window.startTime.split(':')[1]);
              return windowStartMinutes < currentTimeInMinutes;
            }
            return false;
          };

          // Find first non-past window for the new date
          const newWindow = newDayWindows.find((w) => !isPastWindowForDate(w, newDate));

          if (newWindow) {
            // Update form state with new times
            setFormState((prev) => ({
              ...prev,
              timeStarted: newWindow.startTime,
              timeEnded: newWindow.endTime,
            }));
          }
          // If all windows are past, don't update form state - let the UI show the issue
        }
      }

      // Force refetch ALL data for the new date
      refetchCalendar();
      refetchCallWindows();
      refetchDepartments();
      refetchUsers();

      onChangeDate?.(newDate);
    },
    [
      isEditing,
      existingEvent,
      callWindows,
      mergeDirtyFields,
      onChangeDate,
      refetchCalendar,
      refetchCallWindows,
      refetchDepartments,
      refetchUsers,
      setFormState,
    ],
  );

  // Step 3 form changes → draft. Hoisted out of renderStep() and memoized so the
  // ServiceCallDetails watch subscription (which depends on this callback) isn't
  // torn down and rebuilt on every parent render — that churn risked dropping a
  // field change (e.g. Sector) from the draft, which the wizard would later
  // restore as stale state. setCurrentWindowId is a stable state setter.
  const handleFormDataChange = useCallback(
    (updatedData: Partial<Event>, dirtyFields?: DirtyModelFields<Record<string, unknown>>) => {
      if (updatedData.timeStarted !== undefined || updatedData.timeEnded !== undefined) {
        setCurrentWindowId(0);
      }
      updateDraft(updatedData, dirtyFields);
    },
    [updateDraft],
  );

  if (loading) {
    return (
      <div className="flex w-full justify-center py-10">
        <LoadingIcon className="size-8" />
      </div>
    );
  }

  // Build scheduling label for banner (using shared memoized dayWindowsAscending)
  const earliestWindow = dayWindowsAscending[0];
  const selectedHeaderWindow = effectiveTimeWindow || earliestWindow;

  // Banner texts
  const dateText = format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy');
  const isUsingCustomTime = timeWindowType === 'custom';

  const timeText =
    isUsingCustomTime && formState.timeStarted && formState.timeEnded
      ? `${formatTimeForDisplay(formState.timeStarted)}–${formatTimeForDisplay(formState.timeEnded)} (Custom)`
      : selectedHeaderWindow
        ? formatTimeWindowLabel(selectedHeaderWindow.startTime, selectedHeaderWindow.endTime)
        : '';
  const shouldUseLabel =
    windowLabel &&
    (effectiveTimeWindow?.id === initialTimeWindowId || !effectiveTimeWindow) &&
    !isUsingCustomTime;
  const headerText = `${dateText}${shouldUseLabel ? ' • ' + windowLabel : timeText ? ' • ' + timeText : ''}`;

  // When the property (or customer) changes mid-flow for a NEW job, the callback
  // context is property-scoped and no longer valid — clear it so a stale
  // callbackOriginalId can't be submitted against an unrelated property and the
  // callback prompt re-evaluates for the newly selected property.
  const resetCallbackForNewJob = () => {
    if (isEditing) return;
    setFormState((prev) => ({ ...prev, isCallback: 0, callbackOriginalId: 0 }));
  };

  const renderStep = () => {
    if (step === 'customer') {
      return (
        <CustomerStep
          initialSearch={customerSearch}
          onSearchChange={setCustomerSearch}
          onSelectCustomer={(c) => {
            setCustomer(c);
            setProperty(null);
            setJustCreatedCustomer(false);
            setStep('property');
          }}
          onCreateCustomer={(u, propertyData) => {
            setCustomer(u);
            setProperty(null);
            setJustCreatedCustomer(true);

            const prop = Property.create({
              userId: u.id,
              address: propertyData.streetAddress,
              city: propertyData.city,
              state: propertyData.state,
              zip: propertyData.zipCode,
              additionalAddressLine: propertyData.storeNumber ?? '',
              notes: propertyData.notes ?? '',
              propertyOwnershipTypeId: propertyData.propertyOwnershipTypeId,
              isActive: 1,
            });
            propertyCreateMutation.mutate(prop, {
              onSuccess: (createdProp) => {
                if (!createdProp) return;
                setProperty(createdProp);
                resetCallbackForNewJob();
                setStep('review');
              },
              onError: () => {
                toast({
                  title: 'Failed to create property',
                  description: 'Please add the service address on the next screen.',
                  variant: 'destructive',
                });
                setStep('property');
              },
            });
          }}
          onSelectProperty={(p, customer) => {
            setProperty(p);
            setCustomer(customer!);
            setJustCreatedCustomer(false);
            resetCallbackForNewJob();
            setStep('review');
          }}
        />
      );
    }

    if (step === 'property') {
      if (!customer) return null;
      return (
        <PropertyStep
          customer={customer}
          onSelectProperty={(p) => {
            setProperty(p);
            resetCallbackForNewJob();
            setStep('review');
          }}
          onCreateProperty={(p) => {
            setProperty(p);
            resetCallbackForNewJob();
            if (justCreatedCustomer && customer) {
              updateCustomerMutation.mutate(
                User.create({
                  id: customer.id,
                  address: p.address,
                  city: p.city,
                  state: p.state,
                  zip: p.zip,
                  fieldMask: ['Address', 'City', 'State', 'Zip'],
                }),
                {
                  onError: (e) => console.error('Failed to backfill customer billing address', e),
                },
              );
              setCustomer({
                ...customer,
                address: p.address,
                city: p.city,
                state: p.state,
                zip: p.zip,
              });
            }
            setStep('review');
          }}
          onBack={() => {
            setCustomer(null);
            setProperty(null);
            setJustCreatedCustomer(false);
            setStep('customer');
          }}
          defaultTab={justCreatedCustomer ? 'new' : 'select'}
          newJob={!isEditing}
        />
      );
    }

    // Review step — customer/property context before scheduling
    if (step === 'review') {
      if (!customer || !property) return null;
      return (
        <ReviewStep
          customer={customer}
          property={property}
          onBack={() => setStep('property')}
          onContinue={(type) => {
            setJobType(type);
            onJobTypeChange?.(type);
            setStep('details');
          }}
          onNotificationSaved={handleNotificationSaved}
          onEndWithoutScheduling={handleCancel}
          allowedJobType={allowedJobType}
          isCallbackFlagged={formState.isCallback === 1}
          onMarkCallback={(originalEventId) =>
            updateDraft({ isCallback: 1, callbackOriginalId: originalEventId })
          }
          callbackPromptDismissed={callbackPromptDismissed}
          onDismissCallbackPrompt={() => setCallbackPromptDismissed(true)}
        />
      );
    }

    if (!customer || !property) return null;

    const handleJobSubmit = async (
      eventPartial: Partial<Event>,
      dirtyFields?: DirtyModelFields<Record<string, unknown>>,
      customerLevelData?: CustomerLevelData,
      internalNotes?: string,
    ) => {
      // Don't wrap in triggerGlobalLoading here - let onJobCreated handle it
      await submitJob(eventPartial, dirtyFields, customerLevelData, internalNotes);
    };

    // Handle project form submission
    const handleProjectSubmit = async (projectData: ProjectFormValues) => {
      await submitProject(projectData, customer);
    };

    // Render Project Details if project type is selected
    if (jobType === 'project') {
      return (
        <ProjectDetails
          customer={customer}
          property={property}
          onBack={() => setStep('review')}
          onSubmit={handleProjectSubmit}
          onCancel={handleCancel}
          hideCancelButton={hideCancelButton}
          loading={isSubmitting}
        />
      );
    }

    // Render Service Call Details (default)
    return (
      <ServiceCallDetails
        date={selectedDate}
        selectedTimeWindow={effectiveTimeWindow}
        isCustomTime={timeWindowType === 'custom'}
        callWindows={callWindowsForDept}
        windowEventCounts={windowEventCounts}
        windowTechCounts={windowTechCounts}
        windowJobTypeBreakdown={windowJobTypeBreakdown}
        windowMaintConfirmedCounts={windowMaintConfirmedCounts}
        dayJobTypeConfirmedCounts={dayJobTypeConfirmedCounts}
        capacityDate={capacityDate}
        dateBreakout={dateBreakout}
        customer={customer}
        property={property}
        departments={departments}
        technicians={technicians}
        onBack={isEditing ? undefined : () => setStep('review')}
        onChangeCustomer={isEditing ? undefined : () => setStep('customer')}
        onChangeProperty={isEditing ? undefined : () => setStep('property')}
        onSubmit={handleJobSubmit}
        loading={loading}
        onCancel={handleCancel}
        hideCancelButton={hideCancelButton}
        onJobCreated={onJobCreated}
        existingEvent={existingEvent}
        onTimeWindowChange={(windowId: number) => {
          setCurrentWindowId(windowId);
        }}
        onDateChange={handleDateChange}
        onFormDataChange={handleFormDataChange}
        formState={formState}
        isCapacityLoading={isCalendarLoading}
        hideCallWindowSelection={hideCallWindowSelection}
        preservedDirtyFields={preservedDirtyFields}
        windowBlocks={windowBlocks}
        defaultDepartmentId={defaultDepartmentId}
        defaultTradeId={defaultTradeId}
        bookingTrade={bookingTrade}
        callbackPromptDismissed={callbackPromptDismissed}
        onDismissCallbackPrompt={() => setCallbackPromptDismissed(true)}
      />
    );
  };

  // Determine if we should show the scheduling banner
  const shouldShowBanner = showSchedulingBanner ?? !!date;

  // Compute block state for the effective time window
  const windowFullyBlocked =
    shouldShowBanner && effectiveTimeWindow
      ? isFullyBlocked(
          windowBlocks,
          effectiveTimeWindow.startTime,
          effectiveTimeWindow.endTime,
          totalJobTypeCount,
        )
      : false;
  const partiallyBlockedJobTypeIds =
    shouldShowBanner && effectiveTimeWindow && !windowFullyBlocked
      ? getBlockedJobTypesForWindow(
          windowBlocks,
          effectiveTimeWindow.startTime,
          effectiveTimeWindow.endTime,
        )
      : [];
  const isPartiallyBlocked = partiallyBlockedJobTypeIds.length > 0;
  const partiallyBlockedJobTypeNames = partiallyBlockedJobTypeIds
    .map((id) => {
      const match = jobTypesData?.find((jt) => Number(jt.id) === Number(id));
      return match?.name ?? `Job Type #${id}`;
    })
    .join(', ');

  // Capacity state for the window this job will land in — the wizard always resolves one
  // when it opens, so Step 1 can warn that scheduling would overbook instead of leaving the
  // user to discover it on Step 3. Same rule as the Step 3 window cards
  // (CallWindowAvailability): effective max is this department's override if it has one,
  // else the window's own value, and windowCount 0 means "no max configured".
  const capacityDeptId = formState.departmentId ?? defaultDepartmentId;
  const capacityWindow = timeWindowType === 'preset' ? effectiveTimeWindow : null;
  const capacityMaxJobs = capacityWindow
    ? resolveWindowLimits(
        capacityWindow,
        windowLimitOverrides,
        windowLimitDateOverrides,
        capacityDate,
      ).windowCount
    : 0;
  const capacityJobsBooked =
    capacityWindow && capacityDeptId
      ? windowEventCounts[capacityWindow.id]?.[capacityDeptId] ?? 0
      : 0;
  // A job already in this window takes no new slot, so keeping it here is not an overbook.
  // Resolved the same way as the Step 3 card: latest-starting window that contains the event.
  const capacityWindowHoldsEditedEvent =
    !!existingEvent &&
    !!capacityWindow &&
    existingEvent.dateStarted?.split(' ')[0] === selectedDate &&
    (existingEvent.departmentId || 0) === (capacityDeptId || 0) &&
    resolveEventWindow(existingEvent, dayWindowsDescending)?.id === capacityWindow.id;
  const windowAtCapacity =
    !!capacityWindow &&
    !!capacityDeptId &&
    !windowFullyBlocked &&
    !capacityWindowHoldsEditedEvent &&
    capacityMaxJobs > 0 &&
    capacityJobsBooked >= capacityMaxJobs;

  return (
    <div
      className={`theme-light flex w-full min-w-0 flex-col ${compactChrome ? 'gap-4' : 'gap-6'}`}
    >
      {/* Header — frozen/pinned in the scheduler; plain stacked layout elsewhere.
          On the Schedule step it stays pinned but lays out like production. */}
      <div
        className={
          compact
            ? compactChrome
              ? 'sticky top-0 z-20 -mx-6 flex flex-col border-b border-gray-200 bg-white'
              : 'sticky top-0 z-20 -mx-6 flex flex-col gap-4 border-b border-gray-200 bg-white px-6 pb-4'
            : 'flex flex-col gap-6'
        }
      >
        {/* Title + Step Indicator */}
        {(title || !isEditing) && (
          <div
            className={`flex items-center justify-between ${
              // Inside the pinned full-bleed header the row needs clearance from
              // the dialog close (X); on the Schedule step the wrapper's px-6
              // supplies the left inset.
              compact
                ? compactChrome
                  ? 'border-b border-gray-200 py-3 pl-6 pr-12'
                  : 'pr-6 pt-3'
                : 'pr-6'
            }`}
          >
            {title && (
              <h2
                className={`font-semibold text-gray-900 ${compactChrome ? 'text-base' : 'text-lg'}`}
              >
                {title}
              </h2>
            )}
            {!isEditing && (
              <StepIndicator
                currentStep={getDisplayStep()}
                onStepClick={
                  compact
                    ? (target) => {
                        // Navigate back to an earlier, completed step. "Search"
                        // returns to customer search; selections persist.
                        if (target === 'search') setStep('customer');
                        else if (target === 'review') setStep('review');
                      }
                    : undefined
                }
              />
            )}
          </div>
        )}

        {/* Header banner - only show when date is provided */}
        {shouldShowBanner && (
          <div
            className={`flex items-center justify-between ${
              windowFullyBlocked
                ? 'rounded-md border border-red-200 bg-red-50 p-4'
                : compactChrome
                  ? 'border-b border-gray-200 px-6 py-3'
                  : 'rounded-md border bg-white p-4'
            }`}
          >
            <p
              className={`font-medium ${compactChrome ? 'text-sm' : ''} ${
                windowFullyBlocked
                  ? 'text-red-700'
                  : compactChrome
                    ? 'text-blue-600'
                    : 'text-blue-700'
              }`}
            >
              Scheduling for: {headerText}
              {windowFullyBlocked && ' (Blocked)'}
            </p>
            <div className="flex items-center gap-2">
              {canBlock &&
                effectiveTimeWindow &&
                (() => {
                  return (
                    <Button
                      onClick={handleToggleBlock}
                      variant={windowFullyBlocked ? 'outline' : 'destructive'}
                      size="sm"
                      disabled={isBlockBusy}
                      className={
                        windowFullyBlocked
                          ? 'border-green-500 text-green-700 hover:bg-green-50'
                          : undefined
                      }
                    >
                      {isBlockBusy ? (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      ) : windowFullyBlocked ? (
                        <>
                          <Unlock className="mr-1.5 h-4 w-4" />
                          Unblock Window
                        </>
                      ) : (
                        <>
                          <Lock className="mr-1.5 h-4 w-4" />
                          Block Window
                        </>
                      )}
                    </Button>
                  );
                })()}
              {/* Change Date calendar popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={
                      compactChrome
                        ? 'text-xs font-medium'
                        : 'border-blue-600 text-blue-600 hover:bg-blue-50'
                    }
                  >
                    Change Date
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="theme-light w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={parseISO(selectedDate)}
                    onSelect={(d) => {
                      if (!d) return;
                      const newDate = format(d, 'yyyy-MM-dd');
                      handleDateChange(newDate);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}

        {/* Services Needed — visible on every step for new jobs, writes directly to draft */}
        {!isEditing && (
          <div className={compactChrome ? 'px-6 py-3' : undefined}>
            <div
              className={`flex items-center gap-3 rounded-md border px-4 py-2 ${
                compactChrome ? 'border-gray-200 bg-gray-50' : 'border-gray-300'
              } ${windowFullyBlocked ? 'pointer-events-none select-none opacity-50' : ''}`}
            >
              {compactChrome ? (
                <Search className="h-4 w-4 shrink-0 text-gray-400" />
              ) : (
                <Pencil className="h-4 w-4 shrink-0 text-gray-400" />
              )}
              <Input
                value={formState.description ?? ''}
                onChange={(e) => updateDraft({ description: e.target.value })}
                placeholder="Services needed..."
                className={`h-auto border-0 p-0 shadow-none focus-visible:ring-0 ${
                  compactChrome ? 'bg-transparent text-sm' : ''
                }`}
              />
              <span className="shrink-0 text-xs text-gray-400">Carries to Step 3</span>
            </div>
          </div>
        )}
      </div>

      {/* At-capacity notice for the default window — Search step only; Step 3 shows the same
          state per window on the slot cards. Soft limit: scheduling is still allowed. */}
      {(step === 'customer' || step === 'property') && windowAtCapacity && (
        <div className="rounded-md border border-amber-400 bg-[#fefde9] px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Window at capacity — {capacityJobsBooked} of {capacityMaxJobs} jobs scheduled
          </p>
          <p className="mt-1 pl-6 text-sm text-amber-700">
            The limit for this time window has been hit.{' '}
            <span className="font-semibold uppercase">Scheduling will overbook this window.</span>
          </p>
        </div>
      )}

      {/* Partial block notice — shown when only specific job types are blocked */}
      {isPartiallyBlocked && (
        <div className="flex items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm font-medium text-yellow-800">
          <Lock className="h-4 w-4 shrink-0" />
          Some job types are blocked for this window:{' '}
          <span className="font-semibold">{partiallyBlockedJobTypeNames}</span>. Other job types can
          still be scheduled.
        </div>
      )}

      {/* Form content — disabled only when window is fully blocked.
          compactChrome hosts zero the dialog's own pb, so add it back here. */}
      <div
        className={`space-y-6 ${compactChrome ? 'pb-6' : ''} ${windowFullyBlocked ? 'pointer-events-none select-none opacity-50' : ''}`}
      >
        {renderStep()}
      </div>

      {/* Full block banner — shown at bottom when window is fully blocked */}
      {windowFullyBlocked && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
          <Lock className="h-4 w-4" />
          This window is blocked. Unblock to schedule new jobs.
        </div>
      )}
    </div>
  );
};

// Export both the new name and the old name for backwards compatibility
export const JobCreationFlow = JobCreationFlowComponent;
